/**
 * AdUnlockPrompt — shown when a Tier 2 feature requires ad viewing to unlock.
 *
 * Two flows, selected by REACT_APP_GAM_AD_TAG_URL:
 *   - IMA (prod): Watch ad → Google SSV pings our backend → backend writes
 *     the Redis unlock key → we poll /freemium-status until it flips true.
 *   - Mock (dev): Watch mock ad → POST /tokens/ad-unlock directly.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RewardedAdPlayer from '../ads/RewardedAdPlayer';
import { getAdNonce, adUnlock, getFreemiumStatus, FreemiumFeatureType } from '../../services/tokenService';
import { useTokens } from '../../contexts/TokenContext';
import { useAuth } from '../../contexts/AuthContext';
import './AdUnlockPrompt.css';

interface AdUnlockPromptProps {
  featureType: FreemiumFeatureType;
  onUnlocked: () => void;
}

const FEATURE_NAMES: Record<FreemiumFeatureType, string> = {
  comprehension_eval: 'freemium.feature.comprehensionEval',
  practice_materials: 'freemium.feature.practiceMaterials',
  vocab_flashcard: 'freemium.feature.vocabFlashcard',
  dictation: 'freemium.feature.dictation',
  shadowing: 'freemium.feature.shadowing',
  quiz: 'freemium.feature.quiz',
  writing: 'freemium.feature.writing',
  ai_question: 'freemium.feature.aiQuestion',
  learning_progress: 'freemium.feature.learningProgress',
  notes: 'freemium.feature.notes',
  reference: 'freemium.feature.reference',
};

const USE_REAL_IMA = !!process.env.REACT_APP_GAM_AD_TAG_URL;
const SSV_POLL_INTERVAL_MS = 1000;
const SSV_POLL_TIMEOUT_MS = 15_000;

async function pollForSsvUnlock(featureType: FreemiumFeatureType): Promise<boolean> {
  const deadline = Date.now() + SSV_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    try {
      const status = await getFreemiumStatus();
      if (status.isSubscriber || status.features?.[featureType]?.adUnlocked) {
        return true;
      }
    } catch {
      // transient error — keep polling until deadline
    }
    await new Promise(r => setTimeout(r, SSV_POLL_INTERVAL_MS));
  }
  return false;
}

const AdUnlockPrompt: React.FC<AdUnlockPromptProps> = ({ featureType, onUnlocked }) => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { stats } = useTokens();
  const { user } = useAuth();
  const [showAd, setShowAd] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingNonce, setPendingNonce] = useState<string | null>(null);
  const [adPolicy, setAdPolicy] = useState<{ tfcd: 0 | 1; tfua: 0 | 1; npa: 0 | 1 } | undefined>(undefined);

  const featureNameKey = FEATURE_NAMES[featureType] || featureType;

  const handleWatchAd = useCallback(async () => {
    setError(null);
    try {
      const { nonce, adPolicy: policy } = await getAdNonce(featureType);
      setPendingNonce(nonce);
      setAdPolicy(policy);
      setShowAd(true);
    } catch {
      setError(t('freemium.adNonceError', 'Failed to prepare ad. Please try again.'));
    }
  }, [featureType, t]);

  const handleAdReward = useCallback(async () => {
    if (!pendingNonce) return;
    setIsUnlocking(true);
    setError(null);
    try {
      if (USE_REAL_IMA) {
        // SSV mode: Google already pinged our backend in parallel with ad playback.
        // Wait for the Redis key to appear, then proceed.
        const ok = await pollForSsvUnlock(featureType);
        if (!ok) {
          setError(t('freemium.unlockError', 'Failed to unlock. Please try again.'));
          return;
        }
      } else {
        // Mock mode: no SSV — client-attested unlock.
        await adUnlock(featureType, pendingNonce);
      }
      onUnlocked();
    } catch {
      setError(t('freemium.unlockError', 'Failed to unlock. Please try again.'));
    } finally {
      setIsUnlocking(false);
      setShowAd(false);
      setPendingNonce(null);
      setAdPolicy(undefined);
    }
  }, [featureType, pendingNonce, onUnlocked, t]);

  const handleAdClose = useCallback(() => {
    setShowAd(false);
    setPendingNonce(null);
    setAdPolicy(undefined);
  }, []);

  return (
    <div className="ad-unlock-prompt">
      <div className="ad-unlock-prompt-card">
        {/* Play icon */}
        <div className="ad-unlock-prompt-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
          </svg>
        </div>

        <h2 className="ad-unlock-prompt-title">
          {t('freemium.adUnlockTitle', 'Watch a short ad to unlock today\'s access')}
        </h2>

        <p className="ad-unlock-prompt-description">
          {t('freemium.adUnlockDescription', 'Watch a brief ad to unlock {{feature}} for today!', {
            feature: t(featureNameKey, featureType),
          })}
        </p>

        {error && (
          <div className="ad-unlock-prompt-error">{error}</div>
        )}

        <div className="ad-unlock-prompt-actions">
          <button
            className="ad-unlock-prompt-primary"
            onClick={handleWatchAd}
            disabled={isUnlocking}
          >
            {isUnlocking ? '...' : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                <span>{t('freemium.watchAd', 'Watch Ad to Unlock')}</span>
              </>
            )}
          </button>

          <button
            className="ad-unlock-prompt-upgrade"
            onClick={() => navigate('/pricing')}
          >
            {t('freemium.upgradeNoAds', 'Subscribe for ad-free access')}
          </button>

          <button
            className="ad-unlock-prompt-back"
            onClick={() => navigate(-1)}
          >
            {t('freemium.goBack', 'Go Back')}
          </button>
        </div>
      </div>

      {/* Rewarded Ad Player */}
      <RewardedAdPlayer
        isOpen={showAd}
        onClose={handleAdClose}
        onRewardEarned={handleAdReward}
        adDuration={15}
        skipAfter={5}
        currentBalance={stats?.balance || 0}
        rewardAmount={0}
        adPolicy={adPolicy}
        userId={user?.id}
        customData={pendingNonce || undefined}
      />
    </div>
  );
};

export default AdUnlockPrompt;
